import {
  ArrowUpDown,
  Eye,
  Gauge,
  GitCompareArrows,
  Layers,
  Lightbulb,
  LucideIcon,
  MessageSquareQuote,
  Package,
  PlaySquare,
  Plus,
  Tag,
  Target,
} from "lucide-react-native";
import { FC, useMemo } from "react";
import { ScrollView } from "react-native";

import { Box, Card, HStack, Pressable, Text, VStack } from "@/components/ui";
import { useLocalization } from "@/hooks";
import { UseCase } from "@/types/utils";

interface UseCasePickerProps {
  /** Called with the picked `UseCase`, or `null` when the user chose
      "Custom" — the parent then takes responsibility for showing a title
      input (the only required field for a blank section). */
  onSelect: (useCase: UseCase | null) => void;
  /** Disables every card while a request is in flight so the user can't
      double-fire `createSection`. */
  disabled?: boolean;
}

interface PickerEntry {
  description: string;
  icon: LucideIcon;
  title: string;
  useCase: UseCase | null;
}

/**
 * Horizontal carousel of use-case "blocks" surfaced when the author hits
 * "Add Section" in the regular study editor. Selecting a block sends only
 * `{ use_case }` to the backend, which materializes the matching template
 * (title, description, stimuli, config, and the question battery); the
 * trailing "Custom" card opts out of templating and falls back to the
 * legacy title-only flow.
 *
 * Card copy reuses the `dashboard.useCases.*` translation keys already
 * shipped for the quick-insights picker so we don't fork a second copy
 * deck for the same set of use cases.
 */
const UseCasePicker: FC<UseCasePickerProps> = ({ disabled, onSelect }) => {
  const { i18n, locale } = useLocalization();

  const entries = useMemo<PickerEntry[]>(() => {
    const useCaseEntries: PickerEntry[] = [
      {
        description: i18n.t("dashboard.useCases.preferenceTestDescription"),
        icon: GitCompareArrows,
        title: i18n.t("dashboard.useCases.preferenceTest"),
        useCase: UseCase.PreferenceTest,
      },
      {
        description: i18n.t("dashboard.useCases.adCreativeDescription"),
        icon: PlaySquare,
        title: i18n.t("dashboard.useCases.adCreative"),
        useCase: UseCase.AdCreative,
      },
      {
        description: i18n.t("dashboard.useCases.awarenessDescription"),
        icon: Eye,
        title: i18n.t("dashboard.useCases.awareness"),
        useCase: UseCase.Awareness,
      },
      {
        description: i18n.t("dashboard.useCases.claimTestDescription"),
        icon: MessageSquareQuote,
        title: i18n.t("dashboard.useCases.claimTest"),
        useCase: UseCase.ClaimTest,
      },
      {
        description: i18n.t("dashboard.useCases.maxDiffDescription"),
        icon: ArrowUpDown,
        title: i18n.t("dashboard.useCases.maxDiff"),
        useCase: UseCase.MaxDiff,
      },
      {
        description: i18n.t("dashboard.useCases.monadicDescription"),
        icon: Lightbulb,
        title: i18n.t("dashboard.useCases.monadic"),
        useCase: UseCase.Monadic,
      },
      {
        description: i18n.t("dashboard.useCases.npsDescription"),
        icon: Gauge,
        title: i18n.t("dashboard.useCases.nps"),
        useCase: UseCase.NPS,
      },
      {
        description: i18n.t("dashboard.useCases.packagingDescription"),
        icon: Package,
        title: i18n.t("dashboard.useCases.packaging"),
        useCase: UseCase.Packaging,
      },
      {
        description: i18n.t("dashboard.useCases.priceElasticityDescription"),
        icon: Tag,
        title: i18n.t("dashboard.useCases.priceElasticity"),
        useCase: UseCase.PriceElasticity,
      },
      {
        description: i18n.t("dashboard.useCases.turfDescription"),
        icon: Target,
        title: i18n.t("dashboard.useCases.turf"),
        useCase: UseCase.TURF,
      },
    ];

    /* Sort templated entries alphabetically (locale-aware) so the carousel
       matches the quick-insights grid; the Custom card is pinned at the
       end as the explicit opt-out. */
    useCaseEntries.sort((a, b) =>
      a.title.localeCompare(b.title, locale, { sensitivity: "base" })
    );

    return [
      ...useCaseEntries,
      {
        description: i18n.t("studies.useCasePicker.customDescription"),
        icon: Layers,
        title: i18n.t("studies.useCasePicker.custom"),
        useCase: null,
      },
    ];
  }, [i18n, locale]);

  return (
    <VStack className="gap-3">
      <Text className="text-sm font-semibold text-typography-800">
        {i18n.t("studies.useCasePicker.title")}
      </Text>
      {/* Mirrors the dashboard "most used tests" carousel: outer ScrollView
          carries the horizontal scroll, the inner HStack adds vertical /
          right padding so the per-card hover lift (`-translate-y-0.5`) and
          shadow don't get cropped by the ScrollView's clipping bounds. */}
      <ScrollView horizontal showsHorizontalScrollIndicator>
        <HStack className="gap-3 py-2 pr-2">
          {entries.map((entry) => {
            const Icon = entry.icon;
            const isCustom = entry.useCase === null;
            return (
              <Pressable
                key={entry.useCase ?? "__custom__"}
                className={`shrink-0 ${disabled ? "opacity-60" : ""}`}
                disabled={disabled}
                onPress={() => onSelect(entry.useCase)}
                style={{ width: 280 }}
              >
                <Card
                  className={`h-full border ${isCustom ? "border-dashed border-outline-300" : "border-outline-100"} rounded-2xl p-4 hover:border-outline-300 hover:shadow-soft-2 hover:-translate-y-0.5 transition-all duration-200`}
                >
                  <VStack className="gap-2.5">
                    <Box
                      className={`w-9 h-9 rounded-lg items-center justify-center ${isCustom ? "bg-outline-100" : "bg-primary-50"}`}
                    >
                      {isCustom ? (
                        <Plus
                          color="rgb(var(--color-typography-600))"
                          size={18}
                        />
                      ) : (
                        <Icon color="rgb(var(--color-primary-600))" size={18} />
                      )}
                    </Box>
                    <VStack className="gap-1">
                      <Text
                        className="text-sm font-semibold text-typography-900"
                        numberOfLines={1}
                      >
                        {entry.title}
                      </Text>
                      <Text
                        className="text-xs text-typography-400 leading-relaxed"
                        numberOfLines={3}
                      >
                        {entry.description}
                      </Text>
                    </VStack>
                  </VStack>
                </Card>
              </Pressable>
            );
          })}
        </HStack>
      </ScrollView>
    </VStack>
  );
};

export default UseCasePicker;
